<template>
  <v-dialog v-model="dialog" max-width="1100">
    <v-card>
      <v-card-title>
        <span class="headline">Gestione Contenitori</span>
      </v-card-title>
      <v-card-text class="pa-0">
        <v-row no-gutters style="min-height: 480px">
          <!-- Colonna sinistra: form -->
          <v-col cols="12" md="5" class="pa-6">
            <div class="d-flex align-center mb-4">
              <span class="text-subtitle-1 font-weight-medium">
                {{ vaso_in_modifica ? 'Modifica Contenitore' : 'Nuovo Contenitore' }}
              </span>
              <v-spacer></v-spacer>
              <v-btn v-if="vaso_in_modifica" text small color="grey" @click="annullaModificaVaso">
                <v-icon small class="mr-1">mdi-close</v-icon>
                Annulla
              </v-btn>
            </div>
            <v-form ref="form" v-model="formValid">
              <v-text-field
                v-model="contenitore.tipo"
                label="Tipo"
                :rules="[(v) => !!v || 'Campo obbligatorio']"
                required
                outlined
                dense
              />
              <v-text-field
                v-model.number="contenitore.diametro"
                label="Diametro"
                type="number"
                :rules="[nonNegativeRule]"
                outlined
                dense
              />
              <v-text-field
                v-model.number="contenitore.volume"
                label="Volume"
                type="number"
                :rules="[nonNegativeRule]"
                outlined
                dense
              />
              <div class="text-body-2 font-weight-medium mb-2">Colori (palette)</div>
              <v-select
                v-model="selectedPaletteHex"
                :items="paletteColori"
                item-text="nome"
                item-value="hex"
                label="Scegli colore"
                outlined
                dense
                hide-details
              >
                <template #selection="{ item }">
                  <div class="d-flex align-center">
                    <span class="swatch mr-2" :style="swatchStyle(item.hex)"></span>
                    <span>{{ item.nome }}</span>
                  </div>
                </template>
                <template #item="{ item }">
                  <div class="d-flex align-center py-1">
                    <span class="swatch mr-2" :style="swatchStyle(item.hex)"></span>
                    <span>{{ item.nome }}</span>
                  </div>
                </template>
              </v-select>
              <div class="d-flex align-center mt-2 mb-2">
                <v-btn color="primary" outlined small @click="addPaletteColor">
                  Aggiungi
                </v-btn>
              </div>
              <div v-if="contenitore.colori.length" class="mb-2">
                <v-chip
                  v-for="(colore, idx) in contenitore.colori"
                  :key="`${colore}-${idx}`"
                  class="mr-2 mb-2"
                  small
                  close
                  @click:close="removeColor(idx)"
                >
                  <span
                    v-if="parseColorEntry(colore).hex"
                    class="swatch mr-1"
                    :style="swatchStyle(parseColorEntry(colore).hex)"
                  ></span>
                  {{ formatColorEntryLabel(colore) }}
                </v-chip>
              </div>
            </v-form>
            <v-alert v-if="saveErrorMessage" type="error" outlined dense class="mt-3">
              {{ saveErrorMessage }}
            </v-alert>
            <div class="d-flex justify-end mt-4">
              <v-btn text @click="closeDialog" class="mr-2">Annulla</v-btn>
              <v-btn
                color="primary"
                :loading="loading_save"
                :disabled="!formValid"
                @click="saveContenitore"
                >{{ vaso_in_modifica ? 'Aggiorna' : 'Salva' }}</v-btn
              >
            </div>
          </v-col>

          <!-- Divisore verticale -->
          <v-divider vertical />

          <!-- Colonna destra: lista vasi -->
          <v-col cols="12" md="7" class="pa-6 lista-col">
            <div class="d-flex align-center text-subtitle-1 font-weight-medium mb-4">
              <v-icon class="mr-2">mdi-bucket-outline</v-icon>
              Vasi disponibili
            </div>
            <v-alert
              v-if="deleteErrorMessage"
              type="error"
              outlined
              dense
              class="mb-3"
            >
              {{ deleteErrorMessage }}
            </v-alert>
            <v-progress-linear v-if="loading_contenitori" indeterminate class="mb-4" />
            <v-alert
              v-else-if="!contenitoriColtivazione.length"
              type="info"
              outlined
              dense
            >
              Nessun contenitore disponibile.
            </v-alert>
            <v-list v-else dense class="py-0 rounded-lg white">
              <v-list-item
                v-for="(item, index) in contenitoriOrdinatiPerDiametro"
                :key="item.id || `contenitore-${index}`"
              >
                <v-list-item-avatar size="30" class="mr-3">
                  <v-icon>mdi-bucket-outline</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ item.tipo }} Ø {{ formatNumber(item.diametro) }}</v-list-item-title>
                  <v-list-item-subtitle>
                    <span v-if="item.volume !== null && item.volume !== undefined" class="grey--text">
                      Volume: {{ formatNumber(item.volume) }} lt.
                    </span>
                  </v-list-item-subtitle>
                  <div class="d-flex align-center text-caption font-weight-medium mt-2 mb-1 grey--text text--darken-2">
                    <v-icon x-small class="mr-1">mdi-palette</v-icon>
                    Colori vaso
                  </div>
                  <div v-if="getColoriVaso(item).length">
                    <v-chip
                      v-for="colore in getColoriVaso(item)"
                      :key="`${item.id || index}-${colore.value}`"
                      class="mr-2 mb-2"
                      small
                      outlined
                    >
                      <span
                        v-if="colore.hex"
                        class="swatch mr-1"
                        :style="swatchStyle(colore.hex)"
                      ></span>
                      {{ colore.label }}
                    </v-chip>
                  </div>
                  <div v-else class="caption grey--text text--darken-1">
                    Nessun colore specificato
                  </div>
                </v-list-item-content>
                <v-list-item-action class="flex-column align-end" style="gap:4px">
                  <v-btn
                    icon
                    small
                    color="primary"
                    :disabled="!item.id"
                    @click="apriModificaVaso(item)"
                  >
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    color="red"
                    :loading="deletingContenitoreId === item.id"
                    :disabled="!item.id || deletingContenitoreId === item.id"
                    @click="deleteContenitore(item)"
                  >
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import colorUtility from "../Mixin/colorUtility";
export default {
  name: "GestioneVasiDialog",
  mixins: [colorUtility],
  data() {
    return {
      dialog: false,
      formValid: false,
      loading_save: false,
      loading_update: false,
      loading_contenitori: false,
      deletingContenitoreId: null,
      deleteErrorMessage: "",
      saveErrorMessage: "",
      vaso_in_modifica: null,
      selectedPaletteHex: "#000000",
      contenitore: {
        tipo: "",
        diametro: null,
        volume: null,
        colori: [],
      },
      paletteColori: [
        { nome: "Rosso", hex: "#F44336" },
        { nome: "Rosa", hex: "#E91E63" },
        { nome: "Viola", hex: "#9C27B0" },
        { nome: "Viola scuro", hex: "#673AB7" },
        { nome: "Indaco", hex: "#3F51B5" },
        { nome: "Blu", hex: "#2196F3" },
        { nome: "Azzurro", hex: "#03A9F4" },
        { nome: "Ciano", hex: "#00BCD4" },
        { nome: "Turchese", hex: "#009688" },
        { nome: "Verde", hex: "#4CAF50" },
        { nome: "Verde chiaro", hex: "#8BC34A" },
        { nome: "Lime", hex: "#CDDC39" },
        { nome: "Giallo", hex: "#FFEB3B" },
        { nome: "Ambra", hex: "#FFC107" },
        { nome: "Arancione", hex: "#FF9800" },
        { nome: "Arancione scuro", hex: "#FF5722" },
        { nome: "Terracotta", hex: "#E2725B" },
        { nome: "Marrone", hex: "#795548" },
        { nome: "Grigio", hex: "#9E9E9E" },
        { nome: "Grigio tortora", hex: "#8A7F80" },
        { nome: "Grigio blu", hex: "#607D8B" },
        { nome: "Bianco", hex: "#FFFFFF" },
        { nome: "Nero", hex: "#000000" },
        { nome: "Rosso 100", hex: "#FFCDD2" },
        { nome: "Rosso 300", hex: "#E57373" },
        { nome: "Rosso 700", hex: "#D32F2F" },
        { nome: "Rosa 100", hex: "#F8BBD0" },
        { nome: "Rosa 300", hex: "#F06292" },
        { nome: "Rosa 700", hex: "#C2185B" },
        { nome: "Viola 100", hex: "#E1BEE7" },
        { nome: "Viola 300", hex: "#BA68C8" },
        { nome: "Viola 700", hex: "#7B1FA2" },
        { nome: "Blu 100", hex: "#BBDEFB" },
        { nome: "Blu 300", hex: "#64B5F6" },
        { nome: "Blu 700", hex: "#1976D2" },
        { nome: "Azzurro 100", hex: "#B3E5FC" },
        { nome: "Azzurro 300", hex: "#4FC3F7" },
        { nome: "Azzurro 700", hex: "#0288D1" },
        { nome: "Turchese 100", hex: "#B2DFDB" },
        { nome: "Turchese 300", hex: "#4DB6AC" },
        { nome: "Turchese 700", hex: "#00796B" },
        { nome: "Verde 100", hex: "#C8E6C9" },
        { nome: "Verde 300", hex: "#81C784" },
        { nome: "Verde 700", hex: "#388E3C" },
        { nome: "Giallo 100", hex: "#FFF9C4" },
        { nome: "Giallo 300", hex: "#FFF176" },
        { nome: "Giallo 700", hex: "#FBC02D" },
        { nome: "Arancione 100", hex: "#FFE0B2" },
        { nome: "Arancione 300", hex: "#FFB74D" },
        { nome: "Arancione 700", hex: "#F57C00" },
        { nome: "Marrone 300", hex: "#A1887F" },
        { nome: "Marrone 700", hex: "#5D4037" },
      ],
    };
  },
  computed: {
    contenitoriColtivazione() {
      return this.$store.getters.getContenitoriColtivazione || [];
    },
    contenitoriOrdinatiPerDiametro() {
      return [...this.contenitoriColtivazione].sort((a, b) => {
        const diametroA = Number(a.diametro);
        const diametroB = Number(b.diametro);

        const validoA = a.diametro !== null && a.diametro !== undefined && !Number.isNaN(diametroA);
        const validoB = b.diametro !== null && b.diametro !== undefined && !Number.isNaN(diametroB);

        if (!validoA && !validoB) {
          return String(a.tipo || "").localeCompare(String(b.tipo || ""));
        }
        if (!validoA) {
          return 1;
        }
        if (!validoB) {
          return -1;
        }
        return diametroA - diametroB;
      });
    },
  },
  methods: {
    openDialog() {
      this.dialog = true;
      this.deleteErrorMessage = "";
      this.fetchContenitori();
    },
    closeDialog() {
      this.dialog = false;
      this.deleteErrorMessage = "";
      this.resetForm();
    },
    fetchContenitori() {
      this.loading_contenitori = true;
      this.$store
        .dispatch("fetchContenitoriColtivazione")
        .finally(() => {
          this.loading_contenitori = false;
        });
    },
    apriModificaVaso(item) {
      this.vaso_in_modifica = item;
      this.saveErrorMessage = "";
      this.contenitore = {
        tipo: item.tipo || "",
        diametro: item.diametro ?? null,
        volume: item.volume ?? null,
        colori: [...(item.colori || [])],
      };
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation();
      });
    },
    annullaModificaVaso() {
      this.vaso_in_modifica = null;
      this.saveErrorMessage = "";
      this.resetForm();
    },
    formatColorEntryLabel(entry) {
      const parsed = this.parseColorEntry(entry);
      return parsed ? parsed.label : "";
    },
    addPaletteColor() {
      const hex = this.normalizeHexColor(this.selectedPaletteHex);
      if (!hex) {
        return;
      }
      const alreadyExists = (this.contenitore.colori || [])
        .map((colore) => this.parseColorEntry(colore))
        .some((parsed) => parsed && parsed.hex === hex);

      if (!alreadyExists) {
        this.contenitore.colori.push(hex);
      }
    },
    removeColor(index) {
      this.contenitore.colori.splice(index, 1);
    },
    deleteContenitore(item) {
      if (!item || !item.id) {
        return;
      }

      this.deleteErrorMessage = "";
      this.deletingContenitoreId = item.id;

      this.$store
        .dispatch("deleteContenitoreColtivazione", item.id)
        .catch((error) => {
          const status = error && error.response ? error.response.status : null;
          if (status === 403) {
            this.deleteErrorMessage =
              "Operazione non consentita: non hai i permessi per eliminare questo contenitore.";
          } else {
            this.deleteErrorMessage =
              "Errore durante l'eliminazione del contenitore. Riprova.";
          }
        })
        .finally(() => {
          this.deletingContenitoreId = null;
        });
    },
    getColoriVaso(item) {
      return (item.colori || [])
        .map((colore) => this.parseColorEntry(colore))
        .filter((colore) => !!colore);
    },
    nonNegativeRule(value) {
      if (value === null || value === "" || value === undefined) {
        return true;
      }
      return Number(value) >= 0 || "Inserire un numero maggiore o uguale a 0";
    },
    saveContenitore() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.loading_save = true;
      this.saveErrorMessage = "";
      const payload = {
        tipo: this.contenitore.tipo,
        diametro:
          this.contenitore.diametro === "" || this.contenitore.diametro === null
            ? null
            : Number(this.contenitore.diametro),
        volume:
          this.contenitore.volume === "" || this.contenitore.volume === null
            ? null
            : Number(this.contenitore.volume),
        colori: (this.contenitore.colori || [])
          .map((colore) => {
            const parsed = this.parseColorEntry(colore);
            if (!parsed || !parsed.hex) {
              return null;
            }
            return parsed.hex;
          })
          .filter((colore) => !!colore),
      };

      if (this.vaso_in_modifica) {
        this.$store
          .dispatch("updateContenitoreColtivazione", {
            id_contenitore: this.vaso_in_modifica.id,
            payload,
          })
          .then(() => {
            this.vaso_in_modifica = null;
            this.resetForm();
          })
          .catch(() => {
            this.saveErrorMessage = "Errore durante l'aggiornamento. Riprova.";
          })
          .finally(() => {
            this.loading_save = false;
          });
      } else {
        this.$store
          .dispatch("saveContenitoreColtivazione", payload)
          .then(() => {
            this.fetchContenitori();
            this.resetForm();
          })
          .catch(() => {
            this.saveErrorMessage = "Errore durante il salvataggio. Riprova.";
          })
          .finally(() => {
            this.loading_save = false;
          });
      }
    },
    resetForm() {
      this.contenitore = {
        tipo: "",
        diametro: null,
        volume: null,
        colori: [],
      };
      this.vaso_in_modifica = null;
      this.saveErrorMessage = "";
      this.selectedPaletteHex = "#000000";
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
  },
};
</script>

<style scoped>
.headline {
  font-weight: bold;
}
.lista-col {
  overflow-y: auto;
  max-height: 560px;
}

.swatch {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.2);
  display: inline-block;
}
</style>
